import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Stack,
    Chip
} from '@mui/material';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth, apiFetch } from '../hooks/useAuth';
import { Context } from '../context/appContext';
import {
    usePunchoutSession,
    usePunchoutCart,
    submitToProcurementSystem,
    fallbackRedirect,
    clearPunchoutClientState,
    maybeLogoutBeforeRedirect
} from './punchout';

/**
 * PunchoutCart Component
 *
 * Handles punchout cart functionality for B2B procurement systems.
 * Supports both cXML and OCI standards for integration with
 * customer procurement systems like SAP Ariba, Coupa, etc.
 */
export const PunchoutCart = ({
    punchoutType = 'cxml',
    returnUrl = null,
    buyerCookie = null,
    onCartTransfer = null
}) => {
    const { user, accountID, logout } = useAuth();
    const { dispatch } = useContext(Context);

    // Session management via custom hook
    const {
        punchoutSession,
        effectivePunchoutType,
        loading: sessionLoading,
        error: sessionError
    } = usePunchoutSession({
        punchoutType,
        returnUrl,
        buyerCookie,
        accountID
    });

    // Cart management via custom hook
    const {
        cart,
        removeFromCart,
        updateQuantity,
        clearCart,
        syncFromSession,
        formatPrice,
        itemCount,
        total
    } = usePunchoutCart({
        sessionId: punchoutSession?.sessionId
    });

    // Transfer state
    const [transferLoading, setTransferLoading] = useState(false);
    const [transferError, setTransferError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [handoffInProgress, setHandoffInProgress] = useState(false);

    // Sync cart from loaded session
    useEffect(() => {
        if (punchoutSession?.cart) {
            syncFromSession(punchoutSession.cart);
        }
    }, [punchoutSession?.cart, syncFromSession]);

    /**
     * Handle redirect to procurement system.
     */
    const handleRedirect = useCallback(async (result) => {
        // Submit to procurement system immediately.
        // Avoid local logout/state mutations before navigation to prevent UI flicker.
        if (result.cxmlResponse || result.ociResponse) {
            // Submit form - this will navigate to procurement system
            submitToProcurementSystem({
                redirectUrl: result.redirectUrl,
                cxmlResponse: result.cxmlResponse,
                ociResponse: result.ociResponse,
                punchoutType: effectivePunchoutType
            });
        } else {
            // Fallback to GET redirect if no cart data (shouldn't happen)
            console.warn('No cart data in response, falling back to GET redirect');
            fallbackRedirect(result.redirectUrl);
        }
    }, [effectivePunchoutType]);

    /**
     * Transfer cart to procurement system.
     */
    const transferCart = async () => {
        try {
            setTransferLoading(true);
            setTransferError(null);

            console.log('🛒 Transfer starting...', { 
                sessionId: punchoutSession?.sessionId, 
                cartItems: Object.keys(cart).length,
                punchoutType: effectivePunchoutType,
                returnUrl: punchoutSession?.returnUrl
            });

            const response = await apiFetch('/app/punchout/transfer', {
                method: 'POST',
                body: JSON.stringify({
                    sessionId: punchoutSession?.sessionId,
                    cart,
                    punchoutType: effectivePunchoutType,
                    returnUrl: punchoutSession?.returnUrl
                })
            });

            const result = await response.json();
            console.log('🛒 Transfer response:', { 
                ok: response.ok, 
                status: response.status,
                hasRedirectUrl: !!result.redirectUrl,
                hasCxmlResponse: !!result.cxmlResponse,
                hasOciResponse: !!result.ociResponse,
                success: result.success
            });

            // Check if transfer was successful BEFORE clearing state
            if (!response.ok || result.success === false) {
                throw new Error(result.message || `Transfer failed (${response.status})`);
            }

            // Verify we have cart data to send back
            if (!result.cxmlResponse && !result.ociResponse) {
                console.error('Transfer response missing cart data:', result);
                throw new Error('No cart data in response');
            }

            if (result.redirectUrl) {
                // Clear persisted punchout state before external navigation,
                // but keep the current UI stable until the browser leaves the page.
                setHandoffInProgress(true);
                clearPunchoutClientState({
                    dispatch,
                    clearCart,
                    userId: user?.uid,
                    preserveUi: true
                });
                console.log('🛒 Redirecting to:', result.redirectUrl);
                await handleRedirect(result);
                return;
            } else {
                // Transfer successful without redirect
                console.log('🛒 Transfer complete (no redirect URL)');

                // Only clean up local state when we remain on this page.
                clearPunchoutClientState({ dispatch, clearCart, userId: user?.uid });
                await maybeLogoutBeforeRedirect(logout);
                setSuccess(true);

                if (onCartTransfer) {
                    onCartTransfer(result);
                }
            }

        } catch (err) {
            console.error('🛒 Transfer error:', err);
            setTransferError(err.message);
        } finally {
            setTransferLoading(false);
        }
    };

    // Combined loading state
    const loading = sessionLoading || transferLoading;
    const error = sessionError || transferError;

    // Loading state (initial session load)
    if (sessionLoading && !punchoutSession) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Initializing punchout session...
                </Typography>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert severity="error" icon={<ErrorIcon />}>
                {error}
            </Alert>
        );
    }

    // Success state
    if (success) {
        return (
            <Alert severity="success" icon={<CheckCircle />}>
                Cart successfully transferred to your procurement system.
            </Alert>
        );
    }

    if (handoffInProgress) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Transferring cart to procurement system...
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Card>
                <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                        <ShoppingCart />
                        <Typography variant="h6">
                            Punchout Cart
                        </Typography>
                        <Chip
                            label={effectivePunchoutType.toUpperCase()}
                            color="primary"
                            size="small"
                        />
                    </Stack>

                    {itemCount === 0 ? (
                        <EmptyCartMessage />
                    ) : (
                        <CartItemsList
                            cart={cart}
                            formatPrice={formatPrice}
                            updateQuantity={updateQuantity}
                            removeFromCart={removeFromCart}
                            total={total}
                        />
                    )}
                </CardContent>
            </Card>

            {itemCount > 0 && (
                <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        size="large"
                        onClick={transferCart}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <ShoppingCart />}
                    >
                        {loading ? 'Transferring...' : 'Transfer to Procurement System'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

/**
 * Empty cart message component.
 */
const EmptyCartMessage = () => (
    <Stack spacing={1}>
        <Typography variant="body1" color="text.secondary">
            Ihr Punchout-Warenkorb ist leer.
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Durchsuchen Sie den Litport-Katalog und fügen Sie Artikel hinzu.
        </Typography>
        <Box pt={1}>
            <Button
                component={Link}
                to="/web/searchmeta"
                variant="contained"
                startIcon={<SearchIcon />}
                sx={{ alignSelf: 'flex-start' }}
            >
                Zur Meta-Suche
            </Button>
        </Box>
    </Stack>
);

/**
 * Cart items list component.
 */
const CartItemsList = ({ cart, formatPrice, updateQuantity, removeFromCart, total }) => (
    <Stack spacing={2}>
        {Object.entries(cart).map(([isbn, item]) => (
            <CartItem
                key={isbn}
                isbn={isbn}
                item={item}
                formatPrice={formatPrice}
                updateQuantity={updateQuantity}
                removeFromCart={removeFromCart}
            />
        ))}

        <Box borderTop={1} borderColor="divider" pt={2}>
            <Typography variant="h6" textAlign="right">
                Total: {formatPrice(total)}
            </Typography>
        </Box>
    </Stack>
);

/**
 * Individual cart item component.
 */
const CartItem = ({ isbn, item, formatPrice, updateQuantity, removeFromCart }) => (
    <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
            <Typography variant="body1" fontWeight="medium">
                {item.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                ISBN: {isbn}
            </Typography>
            <Typography variant="body2">
                {formatPrice(item.price)} each
            </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
            <Button
                size="small"
                onClick={() => updateQuantity(isbn, item.qty - 1)}
                disabled={item.qty <= 1}
            >
                -
            </Button>
            <Typography variant="body1" minWidth="30px" textAlign="center">
                {item.qty}
            </Typography>
            <Button
                size="small"
                onClick={() => updateQuantity(isbn, item.qty + 1)}
            >
                +
            </Button>
            <Button
                size="small"
                color="error"
                onClick={() => removeFromCart(isbn)}
            >
                Remove
            </Button>
        </Box>
    </Box>
);
